import * as yup from 'yup';

export enum Rule {
	LinkScorecard,
	AssignEmployee,
	OnlyAnalystAndTechnical
}

export interface ServiceRequiredAndOwnershipStructureForm {

	serviceRequired: number;
	bbbeeCode: number;
	sectorCode: number;
	scorecardTypeCategory: number;

	numberOfScorecards?: number | null;
	isPartOfGroupStructure?: boolean | null;
	isRecordsCentralised?: boolean | null;
	numberOfConsolidatedScorecards?: number | null;

	claimingBlackOwnership: boolean;
	percentageBlackOwnershipClaimed?: number | null;
	containTrustBboEsop?: boolean | null;
	ownershipStructureElements: number[];
}

export const ServiceRequiredValidationSchema: yup.SchemaOf<ServiceRequiredAndOwnershipStructureForm> =
	yup.object({
		serviceRequired: yup
			.number()
			.required('Service Required is required')
			.typeError('Service Required is required'),
		bbbeeCode: yup
			.number()
			.required('B-BBEE Codes to Apply is required')
			.typeError('B-BBEE Codes to Apply is required'),
		sectorCode: yup
			.number()
			.required('Sector Codes to Apply is required')
			.typeError('Sector Codes to Apply is required'),
		scorecardTypeCategory: yup
			.number()
			.required('Scorecard Type to Apply is required')
			.typeError('Scorecard Type to Apply is required'),

		numberOfScorecards: yup
			.number()
			.integer('Please enter a valid number without decimals')
			.nullable()
			.transform((curr, orig) => (orig === '' ? null : curr))
			.typeError('Number of scorecards must be a valid number')
			.min(0, 'Number of scorecards must be greater than 0')
			.max(20, 'Number of scorecards must be less/equal to 20'),
		isPartOfGroupStructure: yup.boolean().nullable(),
		isRecordsCentralised: yup.boolean().nullable(),
		numberOfConsolidatedScorecards: yup
			.number()
			.when('showNumberOfEntities', {
				is: true, // Apply the rule when showNumberOfEntities is true
				then: yup.number().required("Number of consolidated scorecards is required").nullable(),
				otherwise: yup.number().nullable()
			})
			.integer('Please enter a valid number without decimals')
			.transform((curr, orig) => (orig === '' ? null : curr))
			.typeError('Number of consolidated scorecards must be a valid number')
			.min(1, 'Number of consolidated scorecards must be greater than 0')
			.max(20, 'Number of consolidated scorecards must be less/equal to 20'),

		claimingBlackOwnership: yup
			.boolean()
			.nullable()
			.required('Percentage Black Ownership Claimed is required'),
		percentageBlackOwnershipClaimed: yup
			.number()
			.when('claimingBlackOwnership', {
				is: true, // Apply the rule when claimingBlackOwnership is true
				then: yup.number().required("Percentage Black Ownership Claimed is required").nullable(),
				otherwise: yup.number().nullable()
			})
			.min(1, 'Percentage Black Ownership Claimed must be greater than 0')
			.max(100, 'Percentage Black Ownership Claimed must be less than or equal to 100'
			)
			.typeError('Percentage Black Ownership Claimed must be a valid number')
			.transform((curr, orig) => (orig === '' ? null : curr)),
		containTrustBboEsop: yup.boolean().nullable(),
		ownershipStructureElements: yup.array().of(yup.number().required()),
	});



export interface ServiceRequiredRenderedStructureForm {
	linkedScorecard?: string | null;
	verificationAnalyst?: string | null;
	technicalSignatory?: string | null;
	assignedEmployees: string[];
	startDate?: Date | null;
	completionDate?: Date | null;
	notes?: string | null;
}

export const ServiceRequiredRenderedValidationSchema: yup.SchemaOf<ServiceRequiredRenderedStructureForm> =
	yup.object({
		verificationAnalyst: yup.string().nullable().transform((curr, orig) => orig === 0 ? null : curr),
		technicalSignatory: yup.string().nullable().transform((curr, orig) => orig === 0 ? null : curr),
		assignedEmployees: yup.array()
			.test({
				name: 'assignedEmployees',
				message: 'Please choose assigned employees',
				test: function (value) {
					const rule = this.resolve(yup.ref('$rule'));
					if (rule === Rule.AssignEmployee) {
						return Array.isArray(value) && value.length > 0;
					}
					return true;
				},
			})
			.of(yup.string().nullable()),
		startDate: yup.date().nullable().transform((curr, orig) => orig === '' ? null : curr),
		completionDate: yup.date().nullable().transform((curr, orig) => orig === '' ? null : curr),
		linkedScorecard: yup.string()
			.when('$rule', {
				is: (value: Rule) => value === Rule.LinkScorecard,
				then: yup.string().required('Please select a scorecard to link').typeError('Please select a scorecard to link'),
				otherwise: yup.string().nullable(),
			}),
		notes: yup
			.string()
			.nullable()
			.max(225, 'Max character limit of 225 reached')
	}) as yup.SchemaOf<ServiceRequiredRenderedStructureForm>;
